import React, { useCallback, useEffect, useState } from "react";
import { Download, Eye, Loader2, X } from "lucide-react";
import { toast } from "react-toastify";
import {
  qrtListUserInvoices,
  qrtGetUserInvoice,
  qrtGetUserInvoicePdf,
  qrtUpdateUserInvoice,
  qrtDownloadUserInvoice,
} from "../../apis/qrtAdmin";
import { Pager, Spinner, StatusChip, fmtDateTime, fmtMoney } from "./qrtUi";

const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][0-9A-Z]Z[0-9A-Z]$/;
const TYPE_LABELS = {
  plan: "Plan",
  addon: "Add-on",
  ai_credits: "AI credits",
  wallet: "Wallet",
};

const dateInputValue = (d) => {
  if (!d) return "";
  const s = typeof d === "string" ? d : new Date(d).toISOString();
  return s.slice(0, 10);
};

const money = (n) => parseFloat(Number(n || 0).toFixed(2));

const QrtUserInvoices = ({ userId }) => {
  const [data, setData] = useState({ invoices: [], total: 0 });
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await qrtListUserInvoices(userId, { page: page + 1, limit: 20 });
      setData(res.data?.data || { invoices: [], total: 0 });
    } catch (_) {
      setData({ invoices: [], total: 0 });
      toast.error("Failed to load invoices");
    } finally {
      setLoading(false);
    }
  }, [userId, page]);

  useEffect(() => { load(); }, [load]);

  const handleDownload = async (invoice) => {
    setBusyId(invoice.id);
    try {
      await qrtDownloadUserInvoice(userId, invoice, `${invoice.invoice_number}.pdf`);
      toast.success("Invoice downloaded");
    } catch (_) {
      toast.error("Failed to download invoice");
    } finally {
      setBusyId(null);
    }
  };

  const handlePreview = async (invoice) => {
    setBusyId(invoice.id);
    try {
      const [detailRes, pdfRes] = await Promise.all([
        qrtGetUserInvoice(userId, invoice.id),
        qrtGetUserInvoicePdf(userId, invoice.id),
      ]);
      const blobUrl = URL.createObjectURL(new Blob([pdfRes.data], { type: "application/pdf" }));
      setPreview({ invoice: detailRes.data?.data || invoice, blobUrl });
    } catch (_) {
      toast.error("Failed to open invoice preview");
    } finally {
      setBusyId(null);
    }
  };

  const closePreview = () => {
    if (preview?.blobUrl) URL.revokeObjectURL(preview.blobUrl);
    setPreview(null);
  };

  return (
    <>
      <div className="qrt-card">
        <div className="qrt-card-head"><h3>Invoices</h3></div>
        {loading ? <Spinner /> : (
          <table className="qrt-table">
            <thead>
              <tr>
                <th>Invoice</th>
                <th>Type</th>
                <th>Bill to</th>
                <th className="r">Amount</th>
                <th>Status</th>
                <th className="r">Date</th>
                <th className="r">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(data.invoices || []).map((inv) => (
                <tr key={inv.id}>
                  <td>
                    <div style={{ fontWeight: 600 }}>{inv.invoice_number}</div>
                    <div className="sub">{inv.plan_name || "—"}</div>
                  </td>
                  <td>{TYPE_LABELS[inv.type] || inv.type || "—"}</td>
                  <td>
                    <div>{inv.customer_name || "—"}</div>
                    <div className="sub">{inv.customer_gstin || inv.customer_email || ""}</div>
                  </td>
                  <td className="r" style={{ fontWeight: 700 }}>{fmtMoney(inv.total)}</td>
                  <td><StatusChip value={inv.status || "paid"} /></td>
                  <td className="r sub">{dateInputValue(inv.invoice_date) || fmtDateTime(inv.created_at)}</td>
                  <td className="r">
                    <div className="qrt-inv-actions">
                      <button
                        type="button"
                        className="ping-btn ping-btn-ghost ping-btn-sm"
                        disabled={busyId === inv.id}
                        onClick={() => handlePreview(inv)}
                      >
                        {busyId === inv.id ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />}
                        Preview
                      </button>
                      <button
                        type="button"
                        className="ping-btn ping-btn-green ping-btn-sm"
                        disabled={busyId === inv.id}
                        onClick={() => handleDownload(inv)}
                      >
                        <Download size={14} />
                        Download
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {(data.invoices || []).length === 0 && (
                <tr><td className="empty" colSpan={7}>No invoices for this user</td></tr>
              )}
            </tbody>
          </table>
        )}
        <Pager page={page} total={data.total || 0} pageSize={20} onPage={setPage} />
      </div>

      {preview && (
        <InvoicePreviewModal
          userId={userId}
          preview={preview}
          onClose={closePreview}
          onSaved={async (updated, blobUrl) => {
            if (preview.blobUrl) URL.revokeObjectURL(preview.blobUrl);
            setPreview({ invoice: updated, blobUrl });
            load();
          }}
        />
      )}
    </>
  );
};

export const InvoicePreviewModal = ({ userId, preview, onClose, onSaved }) => {
  const invoice = preview.invoice;
  const [form, setForm] = useState(() => formFromInvoice(invoice));
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    setForm(formFromInvoice(preview.invoice));
  }, [preview.invoice]);

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const setItem = (index, key, value) => {
    setForm((f) => ({
      ...f,
      items: f.items.map((item, i) => (i === index ? { ...item, [key]: value } : item)),
    }));
  };

  const save = async (e) => {
    e.preventDefault();
    const gstin = String(form.customer_gstin || "").trim().toUpperCase().replace(/\s+/g, "");
    if (gstin && !GSTIN_RE.test(gstin)) {
      toast.error("Enter a valid 15-character GST number, or leave it blank.");
      return;
    }
    setSaving(true);
    try {
      const res = await qrtUpdateUserInvoice(userId, invoice.id, {
        invoice_date: form.invoice_date,
        plan_name: form.plan_name,
        customer_name: form.customer_name,
        customer_email: form.customer_email,
        customer_phone: form.customer_phone,
        customer_address: form.customer_address,
        customer_company_name: form.customer_company_name,
        customer_gstin: gstin,
        notes: form.notes,
        items: form.items.map((item) => ({
          id: item.id,
          description: item.description,
          quantity: item.quantity,
          rate: item.rate,
        })),
      });
      const updated = res.data?.data;
      const pdfRes = await qrtGetUserInvoicePdf(userId, invoice.id);
      const blobUrl = URL.createObjectURL(new Blob([pdfRes.data], { type: "application/pdf" }));
      toast.success("Invoice updated");
      await onSaved(updated, blobUrl);
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to save invoice");
    } finally {
      setSaving(false);
    }
  };

  const download = async () => {
    setDownloading(true);
    try {
      await qrtDownloadUserInvoice(userId, invoice, `${invoice.invoice_number}.pdf`);
    } catch (_) {
      toast.error("Failed to download invoice");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <div className="ping-drawer-backdrop" onClick={onClose} />
      <div className="qrt-inv-modal" role="dialog" aria-modal="true" aria-label="Invoice preview">
        <div className="ping-drawer-head">
          <div>
            <h3>{invoice.invoice_number}</h3>
            <p>Preview and edit this invoice. GST is saved for Ping invoicing only.</p>
          </div>
          <button type="button" className="camp-ico" onClick={onClose} aria-label="Close">
            <X size={16} />
          </button>
        </div>

        <div className="qrt-inv-split">
          <form className="qrt-inv-form qrt-form" onSubmit={save}>
            <p className="qrt-inv-label">Bill to</p>
            <label>
              Company name
              <input
                value={form.customer_company_name}
                onChange={(e) => setField("customer_company_name", e.target.value)}
              />
            </label>
            <label>
              Name
              <input
                value={form.customer_name}
                onChange={(e) => setField("customer_name", e.target.value)}
                required
              />
            </label>
            <label>
              Email
              <input
                type="email"
                value={form.customer_email}
                onChange={(e) => setField("customer_email", e.target.value)}
              />
            </label>
            <label>
              Phone
              <input
                value={form.customer_phone}
                onChange={(e) => setField("customer_phone", e.target.value)}
              />
            </label>
            <label>
              Address
              <input
                value={form.customer_address}
                onChange={(e) => setField("customer_address", e.target.value)}
              />
            </label>
            <label>
              GST number
              <input
                value={form.customer_gstin}
                onChange={(e) => setField("customer_gstin", e.target.value.toUpperCase())}
                placeholder="24AAAAA0000A1Z5"
                maxLength={15}
              />
            </label>
            <p className="sub" style={{ margin: "-4px 0 10px" }}>
              Optional. Saved on this invoice and on the user account for future invoices.
            </p>

            <p className="qrt-inv-label">Invoice</p>
            <label>
              Invoice date
              <input
                type="date"
                value={form.invoice_date}
                onChange={(e) => setField("invoice_date", e.target.value)}
              />
            </label>
            <label>
              Plan / description
              <input
                value={form.plan_name}
                onChange={(e) => setField("plan_name", e.target.value)}
              />
            </label>
            <label>
              Notes
              <textarea
                rows={3}
                value={form.notes}
                onChange={(e) => setField("notes", e.target.value)}
              />
            </label>

            <p className="qrt-inv-label">Line items</p>
            {(form.items || []).map((item, index) => {
              const qty = Math.max(1, parseInt(item.quantity, 10) || 1);
              const rate = money(item.rate);
              const amount = money(qty * rate);
              const gst = money(amount * 0.18);
              return (
                <div key={item.id || index} className="qrt-inv-item">
                  <label>
                    Description
                    <input
                      value={item.description}
                      onChange={(e) => setItem(index, "description", e.target.value)}
                    />
                  </label>
                  <div className="qrt-inv-item-row">
                    <label>
                      Qty
                      <input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => setItem(index, "quantity", e.target.value)}
                      />
                    </label>
                    <label>
                      Rate
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.rate}
                        onChange={(e) => setItem(index, "rate", e.target.value)}
                      />
                    </label>
                    <label>
                      Amount
                      <input value={amount.toFixed(2)} readOnly />
                    </label>
                    <label>
                      GST
                      <input value={gst.toFixed(2)} readOnly />
                    </label>
                  </div>
                </div>
              );
            })}

            <div className="qrt-inv-form-foot">
              <button type="button" className="ping-btn ping-btn-ghost" onClick={download} disabled={downloading}>
                {downloading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                Download
              </button>
              <button type="submit" className="ping-btn ping-btn-green" disabled={saving}>
                {saving && <Loader2 size={14} className="animate-spin" />}
                Save changes
              </button>
            </div>
          </form>

          <div className="qrt-inv-preview">
            {preview.blobUrl ? (
              <iframe title="Invoice PDF" src={preview.blobUrl} />
            ) : (
              <Spinner />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

function formFromInvoice(invoice) {
  return {
    invoice_date: dateInputValue(invoice?.invoice_date || invoice?.created_at),
    plan_name: invoice?.plan_name || "",
    customer_name: invoice?.customer_name || "",
    customer_email: invoice?.customer_email || "",
    customer_phone: invoice?.customer_phone || "",
    customer_address: invoice?.customer_address || "",
    customer_company_name: invoice?.customer_company_name || "",
    customer_gstin: invoice?.customer_gstin || "",
    notes: invoice?.notes || "",
    items: (invoice?.items || []).map((item) => ({
      id: item.id,
      description: item.description || "",
      quantity: item.quantity || 1,
      rate: money(item.rate),
    })),
  };
}

export default QrtUserInvoices;
